import type { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import styled from '@emotion/styled';
import type { ContentStatus, Highlight } from '@hotjar/webapp/domains/highlights/model';
import { Link, Loader, tokens } from '@hotjar/hotsauce';
import { Trans, useTranslation } from 'react-i18next';
import { getHighlightUrl } from './SingleHighlight';

const ClipLoader = styled(Loader)`
  margin-right: ${tokens.spacer.x0};
`;

const Wrapper = styled.div`
  text-transform: none;
  display: flex;
  color: ${tokens.color.text_light};
`;

const StyledLink = styled(Link)`
  margin-left: ${tokens.spacer.x0};
  & svg {
    display: none;
  }
`;

function getFooterMessage(status: ContentStatus): string {
  switch (status) {
    case 'creating':
      return 'highlights:recordings.saving';
    case 'done':
      return 'highlights:recordings.saved';
    case 'failed':
    case 'source_deleted':
      return 'highlights:recordings.save-error';
    default:
      return 'highlights:recordings.unknown-status';
  }
}

interface HighlightSubtitleProps {
  siteId: string;
  highlight: Highlight;
}

const LocalizedSavedMessage = ({ siteId, highlight }: HighlightSubtitleProps) => {
  const highlightUrl = getHighlightUrl(siteId, highlight.id);
  return (
    <Trans i18nKey="highlights:recordings.saved">
      <StyledLink isExternal href={highlightUrl} kind="inline">
        Highlight
      </StyledLink>
    </Trans>
  );
};

const RecordingSubtitle = (props: HighlightSubtitleProps) => {
  const { highlight } = props;
  const { t } = useTranslation();

  return (
    <Wrapper>
      <ClipLoader size="m" isLoading={highlight.contentStatus === 'creating'} />
      {highlight.contentStatus === 'done' ? (
        <LocalizedSavedMessage {...props} />
      ) : (
        <>{t(getFooterMessage(highlight.contentStatus))}</>
      )}
    </Wrapper>
  );
};

const HeatmapSubtitle = (props: HighlightSubtitleProps) => {
  return (
    <Wrapper>
      <LocalizedSavedMessage {...props} />
    </Wrapper>
  );
};

export const HighlightSubtitle = (props: HighlightSubtitleProps): ReactJSXElement => {
  const { highlight } = props;

  if (highlight.type === 'recording') {
    return <RecordingSubtitle {...props} />;
  }

  return <HeatmapSubtitle {...props} />;
};
